// @ts-nocheck
import type { Editor } from '@tiptap/react';

// Primitive UI Components
import { Button } from 'containers/Tiptap/components/tiptap-ui-primitive/button';
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuGroup,
  MenuGroupLabel,
  MenuButton,
  MenuButtonArrow,
} from 'containers/Tiptap/components/tiptap-ui-primitive/menu';
import { Separator } from 'containers/Tiptap/components/tiptap-ui-primitive/separator';

// Styles
import 'containers/Tiptap/components/tiptap-ui-primitive/combobox/combobox.scss';

// Tiptap UI
import { TEXT_COLORS, useColorText } from 'containers/Tiptap/components/tiptap-ui/color-text-button';
import { HIGHLIGHT_COLORS, useColorHighlight } from 'containers/Tiptap/components/tiptap-ui/color-highlight-button';
import type { RecentColor } from 'containers/Tiptap/components/tiptap-ui/color-text-popover';
import { getColorByValue, useRecentColors } from 'containers/Tiptap/components/tiptap-ui/color-text-popover';

// Icons
import { PaintBucketIcon } from 'containers/Tiptap/components/tiptap-icons/paint-bucket-icon';
import { ChevronRightIcon } from 'containers/Tiptap/components/tiptap-icons/chevron-right-icon';
import { TextColorSmallIcon } from 'containers/Tiptap/components/tiptap-icons/text-color-small-icon';
import { useTiptapEditor } from 'containers/Tiptap/hooks/use-tiptap-editor';

interface ColorMenuItemProps {
  color: { value: string; label: string };
}

const TextColorMenuItem: React.FC<ColorMenuItemProps> = ({ color }) => {
  const { addRecentColor } = useRecentColors();
  const { isActive, handleColorText, label } = useColorText({
    label: color.label,
    textColor: color.value,
    onApplied: ({ color: appliedColor, label: appliedLabel }) => addRecentColor({ type: 'text', label: appliedLabel, value: appliedColor }),
  });

  return (
    <MenuItem
      render={<Button data-style="ghost" data-active-state={isActive ? 'on' : 'off'} />}
      onClick={handleColorText}
    >
      <span className="tiptap-button-color-text" style={{ color: color.value }}>
        <TextColorSmallIcon className="tiptap-button-icon" style={{ color: color.value, flexGrow: 1 }} />
      </span>
      <span className="tiptap-button-text">{label}</span>
    </MenuItem>
  );
};

const HighlightColorMenuItem: React.FC<ColorMenuItemProps> = ({ color }) => {
  const { addRecentColor } = useRecentColors();
  const { isActive, handleColorHighlight, label } = useColorHighlight({
    label: color.label,
    highlightColor: color.value,
    mode: 'node',
    onApplied: ({ color: appliedColor, label: appliedLabel }) => addRecentColor({ type: 'highlight', label: appliedLabel, value: appliedColor }),
  });

  return (
    <MenuItem
      render={<Button data-style="ghost" data-active-state={isActive ? 'on' : 'off'} />}
      onClick={handleColorHighlight}
    >
      <span className="tiptap-button-highlight" style={{ '--highlight-color': color.value } as React.CSSProperties} />
      <span className="tiptap-button-text">{label}</span>
    </MenuItem>
  );
};

const RecentColorMenuItem: React.FC<{
  colorObj: RecentColor;
}> = ({ colorObj }) => {
  const colorSet = colorObj.type === 'text' ? TEXT_COLORS : HIGHLIGHT_COLORS;
  const color = getColorByValue(colorObj.value, colorSet);

  const ColorComponent = colorObj.type === 'text' ? TextColorMenuItem : HighlightColorMenuItem;

  return <ColorComponent color={color} />;
};

export interface ColorMenuProps {
  editor?: Editor | null;
  /**
   * Custom trigger component. If not provided, uses default paint bucket button.
   */
  trigger?: React.ReactNode;
  /**
   * Label for the color menu trigger
   * @default "Colour"
   */
  label?: string;
  /**
   * Menu placement relative to trigger
   * @default "right"
   */
  placement?: React.ComponentProps<typeof Menu>['placement'];
}

/**
 * Reusable color menu component that provides text and highlight color options.
 * Includes recent colors, text colors, and highlight colors sections.
 */
export const ColorMenu: React.FC<ColorMenuProps> = ({
  editor: providedEditor,
  trigger,
  label = 'Colour',
  placement = 'right',
}) => {
  const { editor } = useTiptapEditor(providedEditor);
  const { recentColors, isInitialized } = useRecentColors();

  const hasColorActions: boolean =
    !!editor?.can().setMark('textStyle') ||
    !!editor?.can().setMark('highlight') ||
    !!editor?.can().toggleNodeBackgroundColor('yellow') ||
    false;

  if (!editor || !hasColorActions) {
    return null;
  }

  return (
    <Menu
      placement={placement}
      trigger={
        trigger || (
          <MenuButton
            render={
              <MenuItem
                render={
                  <Button data-style="ghost">
                    <PaintBucketIcon className="tiptap-button-icon" />
                    <span className="tiptap-button-text">{label}</span>
                    <MenuButtonArrow render={<ChevronRightIcon />} />
                  </Button>
                }
              />
            }
          />
        )
      }
    >
      <MenuContent className="tiptap-combobox-list">
        {/* Recent Colors */}
        {isInitialized && recentColors.length > 0 && (
          <MenuGroup>
            <MenuGroupLabel>Recent colours</MenuGroupLabel>
            {recentColors.map((colorObj) => (
              <RecentColorMenuItem key={colorObj.value} colorObj={colorObj} />
            ))}
            <Separator orientation="horizontal" />
          </MenuGroup>
        )}

        {/* Text Colors */}
        <MenuGroup>
          <MenuGroupLabel>Text colour</MenuGroupLabel>
          {TEXT_COLORS.map((textColor) => (
            <TextColorMenuItem key={textColor.value} color={textColor} />
          ))}
        </MenuGroup>

        <Separator orientation="horizontal" />

        {/* Background Colors */}
        <MenuGroup>
          <MenuGroupLabel>Background colour</MenuGroupLabel>
          {HIGHLIGHT_COLORS.map((highlightColor) => (
            <HighlightColorMenuItem key={highlightColor.value} color={highlightColor} />
          ))}
        </MenuGroup>
      </MenuContent>
    </Menu>
  );
};

export { TextColorMenuItem, HighlightColorMenuItem, RecentColorMenuItem };
